import backendApi from '@/axios/backendApi';
import { useDataStore } from '@/stores/useDataStore';

export async function fetchThresholds() {
  const dataStore = useDataStore();

  try {
    const { data } = await backendApi.get<Threshold[]>('/thresholds');

    const result: Record<string, (number | null)[]> = {};

    for (const threshold of data) {
      const specie = dataStore.species.find((s: Species) => s.id === threshold.specie_id);
      if (specie) {
        result[specie.id] = [
          threshold.IcValue1,
          threshold.IcValue2,
          threshold.IcValue3,
          threshold.ProdMsValue1,
          threshold.ProdMsValue2,
          threshold.ProdMiValue1,
          threshold.ProdMiValue2,
        ];
      }
    }

    dataStore.thresholds = result;
  } catch (err) {
    console.error('Erreur lors de la récupération des seuils:', err);
    throw err; // Relancer l'erreur pour permettre à l'appelant de la gérer
  }
}

// Mettre à jour ou créer un seuil pour une espèce
export async function updateThreshold(specie_id: number, values: (number | null)[]) {
  try {
    // Vérifier que le tableau contient exactement 7 valeurs
    if (values.length !== 7) {
      throw new Error('Le tableau des seuils doit contenir exactement 7 valeurs');
    }

    const payload: ThresholdUpdate = {
      specie_id,
      IcValue1: values[0],
      IcValue2: values[1],
      IcValue3: values[2],
      ProdMsValue1: values[3],
      ProdMsValue2: values[4],
      ProdMiValue1: values[5],
      ProdMiValue2: values[6],
    };

    const { data } = await backendApi.post<Threshold>('/thresholds', payload);
    return data; // Retourner les données mises à jour pour une utilisation ultérieure
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Erreur lors de l'envoi des seuils pour specie_id ${specie_id}: ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );
    } else {
      console.error(`Erreur réseau ou autre lors de l'envoi des seuils pour specie_id ${specie_id}:`, error.message);
    }
    throw error; // Relancer l'erreur pour permettre à l'appelant de la gérer
  }
}

export const fetchManualValues = async () => {
  const dataStore = useDataStore();
  try {
    const { data } = await backendApi.get('/manualvalues');

    const mapped: Record<number, ManualValue> = {};
    for (const entry of data) {
      mapped[entry.culture_id] = entry;
    }

    dataStore.manualValues = mapped;
  } catch (error: any) {
    if (error.response) {
      console.error(
        'Erreur lors de la récupération des valeurs manuelles :',
        error.response.status,
        error.response.statusText,
        error.response.data
      );
    } else {
      console.error('Erreur réseau ou autre lors de la récupération des valeurs manuelles:', error.message);
    }
  }
};

export const createOrUpdateManualValue = async (manualValue: ManualValue) => {
  try {
    const { data } = await backendApi.post('/manualvalues', manualValue);
    return data;
  } catch (error: any) {
    if (error.response) {
      console.error(
        "Erreur lors de la création ou mise à jour de la valeur manuelle :",
        error.response.status,
        error.response.statusText,
        error.response.data
      );
    } else {
      console.error("Erreur réseau ou autre lors de la création ou mise à jour de la valeur manuelle :", error.message);
    }
    throw error;
  }
};

export const fetchCommentaire = async () => {
  const dataStore = useDataStore();
  try {
    const { data } = await backendApi.get('/commentaire');
    dataStore.commentaire = data.length > 0 ? data[0] : null;
  } catch (error: any) {
    if (error.response) {
      console.error(
        'Erreur lors de la récupération du commentaire :',
        error.response.status,
        error.response.statusText,
        error.response.data
      );
    } else {
      console.error('Erreur réseau ou autre lors de la récupération du commentaire :', error.message);
    }
  }
};

export const updateCommentaire = async (commentaire: Commentaire) => {
  try {
    const { data } = await backendApi.post('/commentaire', {
      commentaire: commentaire.commentaire,
    });
    return data;
  } catch (error: any) {
    if (error.response) {
      console.error(
        "Erreur lors de la création ou mise à jour du commentaire :",
        error.response.status,
        error.response.statusText,
        error.response.data
      );
    } else {
      console.error("Erreur réseau ou autre :", error.message);
    }
    throw error;
  }
};

export async function updateDB() {
  try {
    const response = await backendApi.post('/update-db');
    const data = response.data;
    return data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to trigger database update';
    console.error('Error triggering DB update:', errorMessage);
    throw error;
  }
}